import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useTokenIssuer } from './TokenIssuerContext';
import { useAssetClass } from './AssetClassContext';
import { assetsApi, assetBulkRegistrationsApi, mintRequestsApi, burnRequestsApi } from '../api/smartTokenizerApi';
import {
  mapAssetToFrontend,
  normalizeSectorType,
  resolveAssetClassForAsset,
  resolveAuthorizedTokenizerIdForSector,
  toAssetCreateRequest,
  toAssetUpdateRequest
} from '../api/smartTokenizerMappers';

const TokenizationContext = createContext();

// Asset Categories with specific metadata templates
export const ASSET_CATEGORIES = {
  TECHNOLOGY: {
    id: 'technology',
    name: 'Technology',
    description: 'Patents, hardware, IP rights, software',
    metadataFields: ['patentNumber', 'inventorName', 'filingDate', 'expiryDate', 'jurisdiction', 'technologyType', 'commercialValue'],
    icon: '💻',
    color: '#4facfe'
  },
  COLLECTIBLES: {
    id: 'collectibles',
    name: 'Collectibles',
    description: 'Toys, comics, memorabilia, rare items',
    metadataFields: ['condition', 'rarity', 'manufacturer', 'year', 'edition', 'provenance', 'authentication'],
    icon: '🏆',
    color: '#ff6b6b'
  },
  ENERGY: {
    id: 'energy',
    name: 'Energy',
    description: 'Renewable energy tokens, carbon credits, oil',
    metadataFields: ['energyType', 'capacity', 'location', 'certificationBody', 'vintageYear', 'methodology', 'co2Equivalent'],
    icon: '⚡',
    color: '#51cf66'
  },
  PRECIOUS_METALS: {
    id: 'preciousMetals',
    name: 'Precious Metals',
    description: 'Gold, silver, platinum bars and coins',
    metadataFields: ['metalType', 'purity', 'weight', 'mint', 'serialNumber', 'assayNumber', 'storageLocation'],
    icon: '🥇',
    color: '#ffd43b'
  },
  REAL_ESTATE: {
    id: 'realEstate',
    name: 'Real Estate',
    description: 'Residential, commercial, land properties',
    metadataFields: ['propertyType', 'squareFootage', 'address', 'yearBuilt', 'bedrooms', 'bathrooms', 'valuation', 'legalDescription'],
    icon: '🏠',
    color: '#845ef7'
  },
  AGRICULTURE: {
    id: 'agriculture',
    name: 'Agriculture',
    description: 'Crops, farmland, livestock, commodities',
    metadataFields: ['cropType', 'weight', 'grade', 'harvestDate', 'origin', 'certifications', 'storageConditions', 'moisture'],
    icon: '🌾',
    color: '#37b24d'
  },
  PATENTS_INNOVATION: {
    id: 'patentsInnovation',
    name: 'Patents & Innovation',
    description: 'Registered IPs and R&D results',
    metadataFields: ['patentType', 'applicationNumber', 'grantDate', 'inventor', 'assignee', 'claims', 'priorArt'],
    icon: '💡',
    color: '#f59f00'
  },
  MUSIC: {
    id: 'music',
    name: 'Music',
    description: 'Tracks, royalties, albums, publishing rights',
    metadataFields: ['trackTitle', 'artist', 'album', 'duration', 'genre', 'isrc', 'publishingRights', 'royaltyShare'],
    icon: '🎵',
    color: '#e64980'
  },
  ENTERTAINMENT: {
    id: 'entertainment',
    name: 'Entertainment',
    description: 'Movie rights, royalties, content',
    metadataFields: ['contentType', 'title', 'duration', 'genre', 'releaseDate', 'director', 'distributionRights', 'revenueShare'],
    icon: '🎬',
    color: '#fa5252'
  },
  ART_CREATIVE: {
    id: 'artCreative',
    name: 'Art & Creative Works',
    description: 'Paintings, digital art, sculptures',
    metadataFields: ['artType', 'artist', 'creationDate', 'medium', 'dimensions', 'provenance', 'authenticity', 'exhibition'],
    icon: '🎨',
    color: '#9775fa'
  },
  AUTOMOTIVE: {
    id: 'automotive',
    name: 'Automotive & Vehicles',
    description: 'Cars, motorcycles, vehicle parts, composable NFTs',
    metadataFields: ['vehicleType', 'make', 'model', 'year', 'vin', 'mileage', 'condition', 'engineNumber', 'horsepower', 'transmission', 'color', 'registrationNumber'],
    icon: '🏎️',
    color: '#4facfe'
  }
};

// Token Types
export const TOKEN_TYPES = {
  FUNGIBLE: {
    id: 'fungible',
    name: 'Fungible Token (FT)',
    description: 'CBC20 - Divisible tokens for commodities and securities',
    standard: 'CBC20',
    divisible: true,
    unique: false
  },
  NON_FUNGIBLE: {
    id: 'nonFungible',
    name: 'Non-Fungible Token (NFT)',
    description: 'CBC721 - Unique tokens for individual assets',
    standard: 'CBC721',
    divisible: false,
    unique: true
  },
  FRACTIONAL_NFT: {
    id: 'fractionalNFT',
    name: 'Fractional NFT',
    description: 'CBC1155 - Fractional ownership of unique assets',
    standard: 'CBC1155',
    divisible: true,
    unique: true
  }
};

// Initial state
const initialState = {
  assets: [],
  tokenizations: [],
  fractions: {},
  vaults: {},
  categories: ASSET_CATEGORIES,
  tokenTypes: TOKEN_TYPES,
  currentTokenization: null,
  loading: false,
  error: null,
  statistics: {
    totalAssets: 0,
    totalValue: 0,
    activeTokenizations: 0,
    totalSupply: {},
    fractionalizedAssets: 0
  }
};

// Action types
const TOKENIZATION_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_ASSETS: 'SET_ASSETS',
  ADD_ASSET: 'ADD_ASSET',
  UPDATE_ASSET: 'UPDATE_ASSET',
  CREATE_TOKENIZATION: 'CREATE_TOKENIZATION',
  UPDATE_TOKENIZATION: 'UPDATE_TOKENIZATION',
  CREATE_FRACTIONALIZATION: 'CREATE_FRACTIONALIZATION',
  UPDATE_FRACTIONS: 'UPDATE_FRACTIONS',
  REDEEM_FRACTIONS: 'REDEEM_FRACTIONS',
  UPDATE_STATISTICS: 'UPDATE_STATISTICS',
  SET_CURRENT_TOKENIZATION: 'SET_CURRENT_TOKENIZATION'
};

// Reducer
const tokenizationReducer = (state, action) => {
  switch (action.type) {
    case TOKENIZATION_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case TOKENIZATION_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };

    case TOKENIZATION_ACTIONS.SET_ASSETS:
      return {
        ...state,
        assets: action.payload,
        loading: false,
        error: null
      };
    
    case TOKENIZATION_ACTIONS.ADD_ASSET:
      return {
        ...state,
        assets: [...state.assets, action.payload],
        loading: false
      };
    
    case TOKENIZATION_ACTIONS.UPDATE_ASSET:
      return {
        ...state,
        assets: state.assets.map(asset => 
          asset.id === action.payload.id ? { ...asset, ...action.payload } : asset
        )
      };
    
    case TOKENIZATION_ACTIONS.CREATE_TOKENIZATION:
      return {
        ...state,
        tokenizations: [...state.tokenizations, action.payload],
        loading: false
      };
    
    case TOKENIZATION_ACTIONS.UPDATE_TOKENIZATION:
      return {
        ...state,
        tokenizations: state.tokenizations.map(tokenization =>
          tokenization.id === action.payload.id ? { ...tokenization, ...action.payload } : tokenization
        )
      };
    
    case TOKENIZATION_ACTIONS.CREATE_FRACTIONALIZATION:
      return {
        ...state,
        fractions: {
          ...state.fractions,
          [action.payload.assetId]: action.payload
        },
        vaults: {
          ...state.vaults,
          [action.payload.vaultId]: action.payload.vault
        }
      };
    
    case TOKENIZATION_ACTIONS.UPDATE_FRACTIONS:
      return {
        ...state,
        fractions: {
          ...state.fractions,
          [action.payload.assetId]: {
            ...state.fractions[action.payload.assetId],
            ...action.payload.updates
          }
        }
      };
    
    case TOKENIZATION_ACTIONS.UPDATE_STATISTICS:
      return {
        ...state,
        statistics: { ...state.statistics, ...action.payload }
      };
    
    case TOKENIZATION_ACTIONS.SET_CURRENT_TOKENIZATION:
      return {
        ...state,
        currentTokenization: action.payload
      };
    
    default:
      return state;
  }
};

// Provider component
const formatApiError = (error) => {
  const d = error.response?.data;
  if (typeof d === 'string') {
    return d;
  }
  if (d?.message) {
    return d.message;
  }
  if (d?.title && d?.detail) {
    return `${d.title}: ${d.detail}`;
  }
  return error.message || 'Request failed';
};

export const TokenizationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(tokenizationReducer, initialState);
  const { currentIssuer, issuers, session } = useTokenIssuer();
  const { assetClasses } = useAssetClass();

  // Generate unique IDs
  const generateId = () => `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  // Generate contract address simulation
  const generateContractAddress = () => `cb${Math.random().toString(16).substr(2, 40)}`;

  const loadAssets = async () => {
    dispatch({ type: TOKENIZATION_ACTIONS.SET_LOADING, payload: true });

    try {
      const { data } = await assetsApi.list();
      const assets = data.map(mapAssetToFrontend);
      dispatch({ type: TOKENIZATION_ACTIONS.SET_ASSETS, payload: assets });
      return assets;
    } catch (error) {
      const status = error.response?.status;
      const message =
        status === 401 || status === 403
          ? null
          : error.response?.data?.message || error.message;

      if (message) {
        dispatch({ type: TOKENIZATION_ACTIONS.SET_ERROR, payload: message });
      } else {
        dispatch({ type: TOKENIZATION_ACTIONS.SET_LOADING, payload: false });
      }

      return [];
    }
  };

  const getAssetById = async (assetId, refresh = false) => {
    const existingAsset = state.assets.find((asset) => asset.id === assetId);
    if (existingAsset && !refresh) {
      return existingAsset;
    }

    const { data } = await assetsApi.getById(assetId);
    const asset = mapAssetToFrontend(data);
    dispatch({
      type: existingAsset ? TOKENIZATION_ACTIONS.UPDATE_ASSET : TOKENIZATION_ACTIONS.ADD_ASSET,
      payload: asset
    });
    return asset;
  };

  const buildTokenizationRecord = (asset, mintedToken, tokenizationConfig = {}) => ({
    id: mintedToken.id,
    assetId: mintedToken.assetId,
    tokenType: tokenizationConfig.tokenType || TOKEN_TYPES.NON_FUNGIBLE.id,
    contractAddress: mintedToken.contractAddress,
    totalSupply: 1,
    decimals: 0,
    symbol: tokenizationConfig.symbol || asset.assetNumber,
    name: tokenizationConfig.name || asset.name,
    metadata: {
      ...asset.metadata,
      tokenizationDate: new Date().toISOString(),
      tokenUri: mintedToken.tokenUri,
      ownerWallet: mintedToken.currentOwnerWallet
    },
    isActive: true,
    holders: [mintedToken.currentOwnerWallet],
    transactions: [mintedToken.mintTransactionHash],
    createdAt: mintedToken.createdAtUtc || new Date().toISOString()
  });

  const upsertTokenization = (tokenization) => {
    const exists = state.tokenizations.some((item) => item.id === tokenization.id);
    dispatch({
      type: exists ? TOKENIZATION_ACTIONS.UPDATE_TOKENIZATION : TOKENIZATION_ACTIONS.CREATE_TOKENIZATION,
      payload: tokenization
    });
  };

  // Create Asset
  /** @param {object} assetData - same shape as single-asset wizard
   * @param {{ quiet?: boolean, skipGlobalLoading?: boolean }} [callOptions] - quiet: no per-asset toasts; skipGlobalLoading: do not toggle context loading (for batch loops) */
  const createAsset = async (assetData, callOptions = {}) => {
    const quiet = Boolean(callOptions.quiet);
    const skipGlobalLoading = Boolean(callOptions.skipGlobalLoading);

    if (!skipGlobalLoading) {
      dispatch({ type: TOKENIZATION_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: TOKENIZATION_ACTIONS.SET_ERROR, payload: null });
    }

    const fail = (error) => {
      const message = error?.response ? formatApiError(error) : error?.message || formatApiError(error);
      dispatch({ type: TOKENIZATION_ACTIONS.SET_ERROR, payload: message });
      if (!quiet) {
        toast.error(message || 'Failed to create asset');
      }
      throw error;
    };

    try {
      const resolvedClass = resolveAssetClassForAsset(assetData, assetClasses);
      if (!resolvedClass) {
        return fail(
          new Error(
            'No asset class matches this selection. Create an asset class for the selected tokenization sector under Asset classes, or pick an existing class in the wizard.'
          )
        );
      }

      const sector = normalizeSectorType(resolvedClass.sector) || 'RealEstate';
      const tokenizerId = resolveAuthorizedTokenizerIdForSector(sector, issuers, currentIssuer);

      let request;
      try {
        request = toAssetCreateRequest(assetData, {
          authorizedTokenizerId: tokenizerId,
          assetClasses
        });
      } catch (err) {
        return fail(err);
      }

      const { data } = await assetsApi.create(request);
      let asset = mapAssetToFrontend(data);

      const failedUploads = [];
      if (Array.isArray(assetData.documents) && assetData.documents.length > 0) {
        for (const document of assetData.documents) {
          if (!document?.file) {
            failedUploads.push(document?.name || 'unknown file');
            continue;
          }
          try {
            const formData = new FormData();
            formData.append('documentType', document.type || 'SupportingDocument');
            formData.append('file', document.file);
            await assetsApi.uploadDocument(asset.id, formData);
          } catch {
            failedUploads.push(document.name || 'document');
          }
        }
      }

      try {
        const reviewResponse = await assetsApi.submitForLegalReview(asset.id);
        asset = mapAssetToFrontend(reviewResponse.data);
      } catch (reviewErr) {
        const revMsg = formatApiError(reviewErr);
        dispatch({ type: TOKENIZATION_ACTIONS.SET_ERROR, payload: revMsg });
        dispatch({ type: TOKENIZATION_ACTIONS.ADD_ASSET, payload: asset });
        try {
          await loadAssets();
        } catch {
          /* ignore list refresh errors */
        }
        if (!quiet) {
          toast.error(`Asset was created, but submitting for legal review failed: ${revMsg}`);
          toast.success(
            `Asset "${asset.name}" is saved as draft. Fix the issue and submit for legal review from Legal reviews or the asset record.`
          );
        }
        return asset;
      }

      dispatch({ type: TOKENIZATION_ACTIONS.ADD_ASSET, payload: asset });

      if (!quiet) {
        if (failedUploads.length > 0) {
          toast.success(
            `Asset "${asset.name}" submitted for legal review. Some documents failed to upload — add them later: ${failedUploads.join(', ')}`
          );
        } else {
          toast.success(`Asset "${asset.name}" created and submitted for legal review`);
        }
      }

      return asset;
    } catch (error) {
      return fail(error);
    } finally {
      if (!skipGlobalLoading) {
        dispatch({ type: TOKENIZATION_ACTIONS.SET_LOADING, payload: false });
      }
    }
  };

  /** GET /api/asset-bulk-registrations — tokenizer/admin sees their bulk batches. */
  const listAssetBulkRegistrations = async () => {
    const { data } = await assetBulkRegistrationsApi.list();
    return data;
  };

  /** GET /api/asset-bulk-registrations/{id} */
  const getAssetBulkRegistrationById = async (id) => {
    const { data } = await assetBulkRegistrationsApi.getById(id);
    return data;
  };

  /** POST /api/asset-bulk-registrations — creates registration + member assets (AuthorizedTokenizer, Admin). */
  const createAssetBulkRegistration = async (payload) => {
    const { data } = await assetBulkRegistrationsApi.create(payload);
    return data;
  };

  /** POST /api/asset-bulk-registrations/{id}/submit-for-legal-review */
  const submitAssetBulkRegistrationForLegalReview = async (id) => {
    const { data } = await assetBulkRegistrationsApi.submitForLegalReview(id);
    return data;
  };

  const submitAssetForLegalReview = async (assetId) => {
    try {
      dispatch({ type: TOKENIZATION_ACTIONS.SET_LOADING, payload: true });
      const { data } = await assetsApi.submitForLegalReview(assetId);
      const asset = mapAssetToFrontend(data);
      dispatch({ type: TOKENIZATION_ACTIONS.UPDATE_ASSET, payload: asset });
      toast.success(`Asset "${asset.name}" submitted for legal review`);
      return asset;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      dispatch({ type: TOKENIZATION_ACTIONS.SET_ERROR, payload: message });
      toast.error(message || 'Failed to submit for legal review');
      throw error;
    } finally {
      dispatch({ type: TOKENIZATION_ACTIONS.SET_LOADING, payload: false });
    }
  };

  const updateAsset = async (assetId, updates) => {
    try {
      dispatch({ type: TOKENIZATION_ACTIONS.SET_LOADING, payload: true });

      const existingAsset = state.assets.find((asset) => asset.id === assetId);
      if (!existingAsset) {
        throw new Error('Asset not found');
      }

      const { data } = await assetsApi.update(assetId, toAssetUpdateRequest(existingAsset, updates));
      const asset = mapAssetToFrontend(data);
      dispatch({ type: TOKENIZATION_ACTIONS.UPDATE_ASSET, payload: asset });
      toast.success(`Asset "${asset.name}" updated`);
      return asset;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      dispatch({ type: TOKENIZATION_ACTIONS.SET_ERROR, payload: message });
      toast.error(message || 'Failed to update asset');
      throw error;
    } finally {
      dispatch({ type: TOKENIZATION_ACTIONS.SET_LOADING, payload: false });
    }
  };

  const prepareMintToken = async (assetId, payload) => {
    const requestedByUserId = currentIssuer?.userId || session?.userId || currentIssuer?.id;
    const { data } = await mintRequestsApi.prepare({
      assetId,
      requestedByUserId,
      ...payload
    });
    return data;
  };

  /** POST /api/mint-requests/bulk-prepare-by-registration — ERC-721 batches from one bulk registration id. */
  const prepareBulkMintByRegistration = async (bulkRegistrationId, payload) => {
    const requestedByUserId = currentIssuer?.userId || session?.userId || currentIssuer?.id;
    const { data } = await mintRequestsApi.bulkPrepareByRegistration({
      bulkRegistrationId,
      requestedByUserId,
      ...payload
    });
    return data;
  };

  const allocateErc721TokenIds = async ({ contractAddress, contractAbi, count }) => {
    const { data } = await mintRequestsApi.allocateErc721TokenIds({
      contractAddress,
      contractAbi,
      count
    });
    return data;
  };

  const allocateErc1155TokenIds = async ({ contractAddress, contractAbi, count }) => {
    const { data } = await mintRequestsApi.allocateErc1155TokenIds({
      contractAddress,
      contractAbi,
      count
    });
    return data;
  };

  const submitMintToken = async (assetId, payload) => {
    const requestedByUserId = currentIssuer?.userId || session?.userId || currentIssuer?.id;
    const { data } = await mintRequestsApi.create({
      assetId,
      requestedByUserId,
      ...payload
    });

    try {
      await getAssetById(assetId, true);
    } catch (error) {
      // Ignore refresh failures here; the caller will poll.
    }

    return data;
  };

  const listMintRequestsForAsset = useCallback(async (assetId) => {
    const { data } = await mintRequestsApi.listHistoryForAsset(assetId);
    return data;
  }, []);

  const listBulkMemberMintHistory = useCallback(async (bulkRegistrationId) => {
    const { data } = await mintRequestsApi.listBulkMemberMintHistory(bulkRegistrationId);
    return data;
  }, []);

  const rebroadcastMintRequest = useCallback(async (mintRequestId) => {
    const { data } = await mintRequestsApi.rebroadcast(mintRequestId);
    return data;
  }, []);

  const prepareBurnToken = async (assetId, payload) => {
    const requestedByUserId = currentIssuer?.userId || session?.userId || currentIssuer?.id;
    const { data } = await burnRequestsApi.prepare({
      assetId,
      requestedByUserId,
      ...payload
    });
    return data;
  };

  const submitBurnToken = async (assetId, payload) => {
    const requestedByUserId = currentIssuer?.userId || session?.userId || currentIssuer?.id;
    const { data } = await burnRequestsApi.create({
      assetId,
      requestedByUserId,
      ...payload
    });
    try {
      await getAssetById(assetId, true);
    } catch {
      /* ignore */
    }
    return data;
  };

  const getMintedTokenByAssetId = async (assetId, tokenizationConfig = null) => {
    try {
      const { data } = await mintRequestsApi.getByAssetId(assetId);
      const asset = await getAssetById(assetId, true);

      upsertTokenization(buildTokenizationRecord(asset, data, tokenizationConfig || {}));
      dispatch({
        type: TOKENIZATION_ACTIONS.UPDATE_ASSET,
        payload: {
          id: assetId,
          tokenizationId: data.id,
          status: 'Minted'
        }
      });

      return data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null;
      }

      throw error;
    }
  };

  const resolveMintRequestedByUserId = () =>
    currentIssuer?.userId || session?.userId || currentIssuer?.id;

  const prepareVaultLock = async (assetId, payload) => {
    const { data } = await assetsApi.prepareVaultLock(assetId, {
      requestedByUserId: resolveMintRequestedByUserId(),
      ...payload
    });
    return data;
  };

  const submitVaultLock = async (assetId, payload) => {
    const { data } = await assetsApi.submitVaultLock(assetId, {
      requestedByUserId: resolveMintRequestedByUserId(),
      ...payload
    });
    try {
      await getAssetById(assetId, true);
    } catch {
      /* ignore */
    }
    return data;
  };

  const getVaultLockByAssetId = async (assetId) => {
    try {
      const { data } = await assetsApi.getVaultLock(assetId);
      return data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  };

  const prepareFractionalShares = async (assetId, payload) => {
    const { data } = await assetsApi.prepareFractionalShares(assetId, {
      requestedByUserId: resolveMintRequestedByUserId(),
      ...payload
    });
    return data;
  };

  const submitFractionalShares = async (assetId, payload) => {
    const { data } = await assetsApi.submitFractionalShares(assetId, {
      requestedByUserId: resolveMintRequestedByUserId(),
      ...payload
    });
    try {
      await getAssetById(assetId, true);
    } catch {
      /* ignore */
    }
    return data;
  };

  const getFractionalSharesByAssetId = async (assetId) => {
    try {
      const { data } = await assetsApi.getFractionalShares(assetId);
      return data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  };

  // Tokenize Asset
  const tokenizeAsset = async (assetId, tokenizationConfig) => {
    try {
      dispatch({ type: TOKENIZATION_ACTIONS.SET_LOADING, payload: true });
      
      const asset = state.assets.find(a => a.id === assetId);
      if (!asset) throw new Error('Asset not found');
      
      const tokenization = {
        id: generateId(),
        assetId,
        tokenType: tokenizationConfig.tokenType,
        contractAddress: generateContractAddress(),
        totalSupply: tokenizationConfig.totalSupply || 1,
        decimals: tokenizationConfig.decimals || 18,
        symbol: tokenizationConfig.symbol,
        name: tokenizationConfig.name,
        metadata: {
          ...asset.metadata,
          tokenizationDate: new Date().toISOString(),
          custodian: tokenizationConfig.custodian
        },
        isActive: true,
        holders: [],
        transactions: [],
        createdAt: new Date().toISOString()
      };
      
      // Simulate smart contract deployment
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      dispatch({ type: TOKENIZATION_ACTIONS.CREATE_TOKENIZATION, payload: tokenization });
      
      // Update asset status
      dispatch({ 
        type: TOKENIZATION_ACTIONS.UPDATE_ASSET, 
        payload: { id: assetId, tokenizationId: tokenization.id, status: 'tokenized' }
      });
      
      toast.success(`Asset tokenized successfully! Contract: ${tokenization.contractAddress.substring(0, 10)}...`);
      
      return tokenization;
    } catch (error) {
      dispatch({ type: TOKENIZATION_ACTIONS.SET_ERROR, payload: error.message });
      toast.error('Failed to tokenize asset');
      throw error;
    }
  };

  // Create Fractional NFT
  const createFractionalNFT = async (nftTokenizationId, fractionConfig) => {
    try {
      dispatch({ type: TOKENIZATION_ACTIONS.SET_LOADING, payload: true });
      
      const nftTokenization = state.tokenizations.find(t => t.id === nftTokenizationId);
      if (!nftTokenization || nftTokenization.tokenType !== TOKEN_TYPES.NON_FUNGIBLE.id) {
        throw new Error('Invalid NFT tokenization');
      }
      
      const vaultId = generateId();
      const fractionTokenId = generateId();
      
      const fractionalization = {
        id: fractionTokenId,
        assetId: nftTokenization.assetId,
        parentNFTId: nftTokenizationId,
        vaultId,
        totalFractions: fractionConfig.totalFractions,
        fractionSymbol: fractionConfig.fractionSymbol,
        fractionName: fractionConfig.fractionName,
        contractAddress: generateContractAddress(),
        isLocked: true,
        redemptionThreshold: fractionConfig.redemptionThreshold || 0.9, // 90% to trigger redemption
        currentHolders: [],
        tradingActive: true,
        createdAt: new Date().toISOString()
      };
      
      const vault = {
        id: vaultId,
        lockedNFT: nftTokenizationId,
        fractionTokenId,
        lockDate: new Date().toISOString(),
        custodian: fractionConfig.custodian,
        status: 'locked'
      };
      
      // Simulate vault creation and NFT locking
      await new Promise(resolve => setTimeout(resolve, 2500));
      
      dispatch({ 
        type: TOKENIZATION_ACTIONS.CREATE_FRACTIONALIZATION, 
        payload: { assetId: nftTokenization.assetId, ...fractionalization, vault }
      });
      
      toast.success(`NFT fractionalized into ${fractionConfig.totalFractions} shares!`);
      
      return fractionalization;
    } catch (error) {
      dispatch({ type: TOKENIZATION_ACTIONS.SET_ERROR, payload: error.message });
      toast.error('Failed to create fractional NFT');
      throw error;
    }
  };

  // Redeem Fractions
  const redeemFractions = async (assetId, holderAddress, fractionAmount) => {
    try {
      dispatch({ type: TOKENIZATION_ACTIONS.SET_LOADING, payload: true });
      
      const fractionalization = state.fractions[assetId];
      if (!fractionalization) throw new Error('Fractionalization not found');
      
      const redemptionRatio = fractionAmount / fractionalization.totalFractions;
      
      if (redemptionRatio >= fractionalization.redemptionThreshold) {
        // Trigger full redemption - unlock NFT
        const vault = state.vaults[fractionalization.vaultId];
        
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        dispatch({
          type: TOKENIZATION_ACTIONS.UPDATE_FRACTIONS,
          payload: {
            assetId,
            updates: {
              isLocked: false,
              tradingActive: false,
              redeemedBy: holderAddress,
              redemptionDate: new Date().toISOString()
            }
          }
        });
        
        toast.success('Redemption threshold met! NFT unlocked and transferred.');
        return { type: 'full_redemption', nftId: vault.lockedNFT };
      } else {
        // Partial redemption - proportional value
        toast.info(`Partial redemption: ${(redemptionRatio * 100).toFixed(2)}% of asset value`);
        return { type: 'partial_redemption', ratio: redemptionRatio };
      }
    } catch (error) {
      dispatch({ type: TOKENIZATION_ACTIONS.SET_ERROR, payload: error.message });
      toast.error('Failed to redeem fractions');
      throw error;
    }
  };

  // Get asset by category
  const getAssetsByCategory = (categoryId) => {
    return state.assets.filter(asset => asset.category === categoryId);
  };

  // Get tokenizations by type
  const getTokenizationsByType = (tokenType) => {
    return state.tokenizations.filter(tokenization => tokenization.tokenType === tokenType);
  };

  // Update statistics
  useEffect(() => {
    const totalAssets = state.assets.length;
    const totalValue = state.assets.reduce((sum, asset) => sum + (asset.valuationUSD || 0), 0);
    const mintedFromApi = state.assets.filter(
      (a) =>
        a.status === 'Minted' ||
        a.status === 'minted' ||
        String(a.status || '').toLowerCase() === 'minted' ||
        Boolean(a.tokenizationId)
    ).length;
    const activeFromSession = state.tokenizations.filter((t) => t.isActive).length;
    const activeTokenizations = Math.max(mintedFromApi, activeFromSession);
    const fractionalizedAssets = state.assets.filter((a) => a.status === 'Fractionalized').length;
    
    const totalSupply = {};
    state.tokenizations.forEach(t => {
      if (totalSupply[t.tokenType]) {
        totalSupply[t.tokenType] += t.totalSupply;
      } else {
        totalSupply[t.tokenType] = t.totalSupply;
      }
    });
    
    dispatch({
      type: TOKENIZATION_ACTIONS.UPDATE_STATISTICS,
      payload: {
        totalAssets,
        totalValue,
        activeTokenizations,
        totalSupply,
        fractionalizedAssets
      }
    });
  }, [state.assets, state.tokenizations, state.fractions]);

  useEffect(() => {
    loadAssets();
  }, [currentIssuer?.id]);

  const value = {
    ...state,
    loadAssets,
    getAssetById,
    createAsset,
    listAssetBulkRegistrations,
    getAssetBulkRegistrationById,
    createAssetBulkRegistration,
    submitAssetBulkRegistrationForLegalReview,
    submitAssetForLegalReview,
    updateAsset,
    prepareMintToken,
    prepareBulkMintByRegistration,
    allocateErc721TokenIds,
    allocateErc1155TokenIds,
    submitMintToken,
    listMintRequestsForAsset,
    listBulkMemberMintHistory,
    rebroadcastMintRequest,
    prepareBurnToken,
    submitBurnToken,
    getMintedTokenByAssetId,
    prepareVaultLock,
    submitVaultLock,
    getVaultLockByAssetId,
    prepareFractionalShares,
    submitFractionalShares,
    getFractionalSharesByAssetId,
    tokenizeAsset,
    createFractionalNFT,
    redeemFractions,
    getAssetsByCategory,
    getTokenizationsByType,
    ASSET_CATEGORIES,
    TOKEN_TYPES
  };

  return (
    <TokenizationContext.Provider value={value}>
      {children}
    </TokenizationContext.Provider>
  );
};

export const useTokenization = () => {
  const context = useContext(TokenizationContext);
  if (!context) {
    throw new Error('useTokenization must be used within a TokenizationProvider');
  }
  return context;
}; 